/**
 * Spreadsheet workspace (`/sheets`) — the editing UI over the Workbook model.
 *
 * File-centric MVP: open an `.xlsx`/`.csv`/`.ods` (parsed server-side by the
 * `office-formats` crate), edit cells in the grid — values and `=` formulas,
 * recomputed live by the client-side engine (`formula.ts`) — and export back.
 * The workbook lives in memory (no local-first persistence yet; documented as
 * pending in docs/SHEETS_SLIDES_PDF_UI.md).
 *
 * Grid interactions: click selects · double-click / Enter / F2 / typing edits ·
 * Enter commits ↓, Tab commits →, Escape cancels · arrows navigate · formula
 * bar edits the selected cell. Rendering is windowed to the first
 * `MAX_VIEW_ROWS×MAX_VIEW_COLS` (data beyond stays intact and exports).
 */
import { type Accessor, createMemo, createSignal, For, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Download, FileUp, Loader2, Minus, Plus, Table2 } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import {
  exportCsv,
  exportOds,
  exportXlsx,
  importCsv,
  importOds,
  importXlsx,
  type Sheet,
  type Workbook,
} from "../lib/office-io";
import { cellName, colName, computeWorkbook } from "../lib/formula";
import {
  addSheet,
  cellInputText,
  deleteCols,
  deleteRows,
  emptyWorkbook,
  insertCols,
  insertRows,
  normalizeForExport,
  parseCellInput,
  removeSheet,
  renameSheet,
  setCell,
  setSheet,
  sheetBounds,
} from "../lib/workbook-ops";
import { downloadBlob, stripExtension } from "../lib/download";

const MIN_VIEW_ROWS = 24;
const MIN_VIEW_COLS = 10;
const MAX_VIEW_ROWS = 200;
const MAX_VIEW_COLS = 40;
const VIEW_ROW_PAD = 6;
const VIEW_COL_PAD = 3;

type SpreadsheetFormat = "xlsx" | "csv" | "ods";

const IMPORTERS: Record<SpreadsheetFormat, (file: Blob) => Promise<Workbook>> = {
  xlsx: importXlsx,
  csv: importCsv,
  ods: importOds,
};
const EXPORTERS: Record<SpreadsheetFormat, (wb: Workbook) => Promise<Blob>> = {
  xlsx: exportXlsx,
  csv: exportCsv,
  ods: exportOds,
};

function isSpreadsheetFormat(ext: string): ext is SpreadsheetFormat {
  return Object.hasOwn(IMPORTERS, ext);
}

interface SheetEditorProps {
  /** Controlled workbook (persisted mode). Omit for the standalone in-memory editor. */
  workbook?: Accessor<Workbook>;
  /** Called with the next workbook on every edit (persisted mode → autosave). */
  onWorkbookChange?: (next: Workbook) => void;
  /** Controlled file/document name (persisted mode → page title). */
  fileName?: Accessor<string>;
  onFileNameChange?: (name: string) => void;
  /** Toolbar slot for the save indicator + version-history button. */
  headerExtras?: JSX.Element;
}

export function SheetEditor(props: SheetEditorProps = {}) {
  const { t } = useI18n();
  const [internalWorkbook, setInternalWorkbook] = createSignal<Workbook>(emptyWorkbook());
  const workbook = (): Workbook => props.workbook?.() ?? internalWorkbook();
  const setWorkbook = (updater: Workbook | ((prev: Workbook) => Workbook)): void => {
    const next = typeof updater === "function" ? updater(workbook()) : updater;
    if (props.onWorkbookChange) {
      props.onWorkbookChange(next);
    } else {
      setInternalWorkbook(next);
    }
  };
  const [activeIdx, setActiveIdx] = createSignal(0);
  const [selection, setSelection] = createSignal<{ row: number; col: number }>({ row: 0, col: 0 });
  const [editing, setEditing] = createSignal<{ row: number; col: number; seed?: string } | null>(
    null,
  );
  const [renaming, setRenaming] = createSignal<number | null>(null);
  const [busy, setBusy] = createSignal<false | "import" | "export">(false);
  const [internalFileName, setInternalFileName] = createSignal("workbook");
  const fileName = (): string => props.fileName?.() ?? internalFileName();
  const setFileName = (name: string): void => {
    if (props.onFileNameChange) {
      props.onFileNameChange(name);
    } else {
      setInternalFileName(name);
    }
  };
  let fileInput: HTMLInputElement | undefined;
  let gridEl: HTMLDivElement | undefined;

  const sheet = createMemo<Sheet>(() => workbook().sheets[activeIdx()] ?? { name: "", rows: [] });
  // Workbook-level so cross-sheet refs (`Sheet2!A1`) resolve live.
  const computed = createMemo(
    () => computeWorkbook(workbook())[activeIdx()] ?? new Map<string, never>(),
  );
  const bounds = createMemo(() => sheetBounds(sheet()));

  const viewRows = createMemo(() =>
    Math.min(Math.max(bounds().rows + VIEW_ROW_PAD, MIN_VIEW_ROWS), MAX_VIEW_ROWS),
  );
  const viewCols = createMemo(() =>
    Math.min(Math.max(bounds().cols + VIEW_COL_PAD, MIN_VIEW_COLS), MAX_VIEW_COLS),
  );
  const truncated = createMemo(
    () => bounds().rows > MAX_VIEW_ROWS || bounds().cols > MAX_VIEW_COLS,
  );

  const selectedCell = createMemo(() => {
    const { row, col } = selection();
    return sheet().rows[row]?.[col];
  });

  const commitInput = (row: number, col: number, raw: string) => {
    const sheetIdx = activeIdx();
    setWorkbook((wb) =>
      setSheet(wb, sheetIdx, setCell(wb.sheets[sheetIdx]!, row, col, parseCellInput(raw))),
    );
  };

  const moveSelection = (dRow: number, dCol: number) => {
    setSelection((sel) => ({
      row: Math.max(0, sel.row + dRow),
      col: Math.max(0, sel.col + dCol),
    }));
  };

  const startEditing = (seed?: string) => {
    const { row, col } = selection();
    setEditing({ row, col, seed });
  };

  const stopEditing = (commit: boolean, value: string, move?: [number, number]) => {
    const edit = editing();
    setEditing(null);
    if (edit && commit) {
      commitInput(edit.row, edit.col, value);
    }
    if (move) {
      moveSelection(move[0], move[1]);
    }
    gridEl?.focus();
  };

  const onGridKeyDown = (e: KeyboardEvent) => {
    if (editing() !== null) {
      return;
    } // the cell input handles its own keys
    switch (e.key) {
      case "ArrowUp": {
        moveSelection(-1, 0);
        e.preventDefault();
        return;
      }
      case "ArrowDown": {
        moveSelection(1, 0);
        e.preventDefault();
        return;
      }
      case "ArrowLeft": {
        moveSelection(0, -1);
        e.preventDefault();
        return;
      }
      case "ArrowRight": {
        moveSelection(0, 1);
        e.preventDefault();
        return;
      }
      case "Enter":
      case "F2": {
        startEditing();
        e.preventDefault();
        return;
      }
      case "Delete":
      case "Backspace": {
        const { row, col } = selection();
        commitInput(row, col, "");
        e.preventDefault();
        return;
      }
      default: {
        // A printable character starts editing with that character as seed.
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          startEditing(e.key);
          e.preventDefault();
        }
      }
    }
  };

  const handleImportFile = async (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const importer = isSpreadsheetFormat(ext) ? IMPORTERS[ext] : undefined;
    if (!importer) {
      showToast(t(msg`Unsupported spreadsheet file`), "error");
      return;
    }
    setBusy("import");
    try {
      const wb = await importer(file);
      setWorkbook(wb.sheets.length > 0 ? wb : emptyWorkbook());
      setActiveIdx(0);
      setSelection({ row: 0, col: 0 });
      setFileName(stripExtension(file.name));
    } catch (err) {
      console.error("[sheets] import failed:", err);
      showToast(t(msg`Could not import that file`), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async (format: SpreadsheetFormat) => {
    if (busy()) {
      return;
    }
    setBusy("export");
    try {
      const blob = await EXPORTERS[format](normalizeForExport(workbook()));
      downloadBlob(blob, `${fileName().trim() || "workbook"}.${format}`);
    } catch (err) {
      console.error("[sheets] export failed:", err);
      showToast(t(msg`Export failed`), "error");
    } finally {
      setBusy(false);
    }
  };

  const isSelected = (r: number, c: number) => {
    const sel = selection();
    return sel.row === r && sel.col === c;
  };

  return (
    <div class="flex h-full min-h-0 flex-col gap-3 p-4" data-testid="sheet-editor">
      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div class="flex flex-wrap items-center gap-2">
        <div class="flex items-center gap-2 mr-auto">
          <Table2 class="h-5 w-5 text-primary shrink-0" />
          <input
            type="text"
            value={fileName()}
            onChange={(e) => setFileName(e.currentTarget.value)}
            data-testid="sheet-name-input"
            aria-label={t(msg`File name`)}
            class="h-9 w-40 rounded-md border border-input bg-background px-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        {props.headerExtras}
        <input
          ref={(el) => (fileInput = el)}
          type="file"
          accept=".xlsx,.csv,.ods"
          class="hidden"
          data-testid="sheet-open-input"
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            if (file) {
              void handleImportFile(file);
            }
            e.currentTarget.value = "";
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          class="gap-1.5"
          data-testid="sheet-open"
          disabled={busy() !== false}
          onClick={() => fileInput?.click()}
        >
          <Show when={busy() === "import"} fallback={<FileUp class="h-4 w-4" />}>
            <Loader2 class="h-4 w-4 animate-spin" />
          </Show>
          {t(msg`Open`)}
        </Button>
        <For each={["xlsx", "csv", "ods"] as const}>
          {(format) => (
            <Button
              variant="ghost"
              size="sm"
              class="gap-1.5"
              data-testid={`sheet-export-${format}`}
              disabled={busy() !== false}
              onClick={() => void handleExport(format)}
            >
              <Download class="h-4 w-4" />
              <span class="uppercase text-xs font-semibold">{format}</span>
            </Button>
          )}
        </For>
      </div>

      {/* ── Formula bar ─────────────────────────────────────────── */}
      <div class="flex items-center gap-2">
        <span
          class="inline-flex h-9 min-w-14 items-center justify-center rounded-md border border-border/50 bg-card/40 px-2 text-xs font-semibold tabular-nums text-muted-foreground"
          data-testid="selection-label"
        >
          {cellName({ row: selection().row, col: selection().col })}
        </span>
        <input
          type="text"
          // Re-key per selection so the uncontrolled value resets on navigate.
          value={cellInputText(selectedCell())}
          data-testid="formula-input"
          aria-label={t(msg`Cell formula or value`)}
          placeholder={t(msg`Type a value or =FORMULA`)}
          class="h-9 flex-1 rounded-md border border-input bg-background px-3 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commitInput(selection().row, selection().col, e.currentTarget.value);
              gridEl?.focus();
              e.preventDefault();
            } else if (e.key === "Escape") {
              e.currentTarget.value = cellInputText(selectedCell());
              gridEl?.focus();
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            // Commit on blur-change too (typing then clicking away).
            commitInput(selection().row, selection().col, e.currentTarget.value);
          }}
        />
      </div>

      {/* ── Structure actions (rows/columns at the selection) ────── */}
      <div class="flex flex-wrap items-center gap-1" data-testid="sheet-structure-actions">
        <Button
          variant="ghost"
          size="sm"
          class="h-7 gap-1 px-2 text-xs"
          data-testid="sheet-insert-row"
          onClick={() => {
            const idx = activeIdx();
            const at = selection().row;
            setWorkbook((wb) => insertRows(wb, idx, at));
          }}
        >
          <Plus class="h-3.5 w-3.5" />
          {t(msg`Insert row`)}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          class="h-7 gap-1 px-2 text-xs"
          data-testid="sheet-delete-row"
          onClick={() => {
            const idx = activeIdx();
            const at = selection().row;
            setWorkbook((wb) => deleteRows(wb, idx, at));
          }}
        >
          <Minus class="h-3.5 w-3.5" />
          {t(msg`Delete row`)}
        </Button>
        <span class="mx-1 h-4 w-px bg-border/60" />
        <Button
          variant="ghost"
          size="sm"
          class="h-7 gap-1 px-2 text-xs"
          data-testid="sheet-insert-col"
          onClick={() => {
            const idx = activeIdx();
            const at = selection().col;
            setWorkbook((wb) => insertCols(wb, idx, at));
          }}
        >
          <Plus class="h-3.5 w-3.5" />
          {t(msg`Insert column`)}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          class="h-7 gap-1 px-2 text-xs"
          data-testid="sheet-delete-col"
          onClick={() => {
            const idx = activeIdx();
            const at = selection().col;
            setWorkbook((wb) => deleteCols(wb, idx, at));
          }}
        >
          <Minus class="h-3.5 w-3.5" />
          {t(msg`Delete column`)}
        </Button>
      </div>

      <Show when={truncated()}>
        <p class="text-xs text-muted-foreground" data-testid="sheet-truncated-notice">
          {t(
            msg`Large sheet: showing the first rows and columns only. All data is kept and exported.`,
          )}
        </p>
      </Show>

      {/* ── Grid ────────────────────────────────────────────────── */}
      <div
        ref={(el) => (gridEl = el)}
        class="min-h-0 flex-1 overflow-auto rounded-lg border border-border/50 bg-card/30 outline-none focus-visible:ring-2 focus-visible:ring-ring"
        tabindex="0"
        role="grid"
        aria-label={t(msg`Spreadsheet grid`)}
        data-testid="sheet-grid"
        onKeyDown={onGridKeyDown}
      >
        <table class="border-collapse text-sm tabular-nums">
          <thead>
            <tr>
              <th class="sticky top-0 left-0 z-20 h-8 w-12 border-b border-r border-border/50 bg-card/95 backdrop-blur" />
              <For each={Array.from({ length: viewCols() }, (_, c) => c)}>
                {(c) => (
                  <th
                    scope="col"
                    class="sticky top-0 z-10 h-8 min-w-24 border-b border-r border-border/40 bg-card/95 px-2 text-center text-xs font-semibold text-muted-foreground backdrop-blur"
                  >
                    {colName(c)}
                  </th>
                )}
              </For>
            </tr>
          </thead>
          <tbody>
            <For each={Array.from({ length: viewRows() }, (_, r) => r)}>
              {(r) => (
                <tr>
                  <th
                    scope="row"
                    class="sticky left-0 z-10 border-b border-r border-border/40 bg-card/95 px-2 text-center text-xs font-semibold text-muted-foreground backdrop-blur"
                  >
                    {r + 1}
                  </th>
                  <For each={Array.from({ length: viewCols() }, (_, c) => c)}>
                    {(c) => {
                      const cellComputed = () => computed().get(`${r},${c}`);
                      const isEditingCell = () => {
                        const edit = editing();
                        return edit !== null && edit.row === r && edit.col === c;
                      };
                      return (
                        <td
                          data-testid={`sheet-cell-${r}-${c}`}
                          aria-selected={isSelected(r, c)}
                          class="h-8 min-w-24 max-w-64 cursor-cell truncate border-b border-r border-border/30 px-2"
                          classList={{
                            "ring-2 ring-inset ring-primary bg-primary/5": isSelected(r, c),
                            "text-right": typeof cellComputed()?.value === "number",
                            "text-destructive font-medium": Boolean(cellComputed()?.error),
                          }}
                          onMouseDown={(e) => {
                            if (isEditingCell()) {
                              return;
                            }
                            // Commit any in-progress edit before moving selection.
                            const edit = editing();
                            if (edit) {
                              const input = document.querySelector<HTMLInputElement>(
                                '[data-testid="cell-input"]',
                              );
                              stopEditing(true, input?.value ?? "");
                            }
                            setSelection({ row: r, col: c });
                            e.preventDefault();
                            gridEl?.focus();
                          }}
                          onDblClick={() => {
                            setSelection({ row: r, col: c });
                            startEditing();
                          }}
                        >
                          <Show when={isEditingCell()} fallback={cellComputed()?.display ?? ""}>
                            <input
                              type="text"
                              data-testid="cell-input"
                              class="h-full w-full border-none bg-transparent font-mono text-sm outline-none"
                              value={editing()?.seed ?? cellInputText(sheet().rows[r]?.[c])}
                              ref={(el) => {
                                queueMicrotask(() => {
                                  el.focus();
                                  el.setSelectionRange(el.value.length, el.value.length);
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  stopEditing(true, e.currentTarget.value, [1, 0]);
                                  e.preventDefault();
                                } else if (e.key === "Tab") {
                                  stopEditing(true, e.currentTarget.value, [0, 1]);
                                  e.preventDefault();
                                } else if (e.key === "Escape") {
                                  stopEditing(false, "");
                                  e.preventDefault();
                                }
                                e.stopPropagation();
                              }}
                              onBlur={(e) => {
                                if (editing()) {
                                  stopEditing(true, e.currentTarget.value);
                                }
                              }}
                            />
                          </Show>
                        </td>
                      );
                    }}
                  </For>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>

      {/* ── Sheet tabs ──────────────────────────────────────────── */}
      <div class="flex flex-wrap items-center gap-1" data-testid="sheet-tabs">
        <For each={workbook().sheets}>
          {(s, i) => (
            <Show
              when={renaming() !== i()}
              fallback={
                <input
                  type="text"
                  value={s.name}
                  data-testid={`sheet-tab-rename-${i()}`}
                  class="h-8 w-28 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  ref={(el) => queueMicrotask(() => el.focus())}
                  onKeyDown={(e) => {
                    const idx = i();
                    const { value } = e.currentTarget;
                    if (e.key === "Enter") {
                      setWorkbook((wb) => renameSheet(wb, idx, value));
                      setRenaming(null);
                    } else if (e.key === "Escape") {
                      setRenaming(null);
                    }
                  }}
                  onBlur={(e) => {
                    const idx = i();
                    const { value } = e.currentTarget;
                    setWorkbook((wb) => renameSheet(wb, idx, value));
                    setRenaming(null);
                  }}
                />
              }
            >
              <button
                type="button"
                data-testid={`sheet-tab-${i()}`}
                class="h-8 rounded-md px-3 text-sm transition-colors"
                classList={{
                  "bg-primary/10 text-primary font-semibold": activeIdx() === i(),
                  "text-muted-foreground hover:bg-secondary/40": activeIdx() !== i(),
                }}
                onClick={() => {
                  setActiveIdx(i());
                  setSelection({ row: 0, col: 0 });
                  setEditing(null);
                }}
                onDblClick={() => setRenaming(i())}
              >
                {s.name}
              </button>
            </Show>
          )}
        </For>
        <Button
          variant="ghost"
          size="sm"
          class="h-8 w-8 p-0"
          data-testid="sheet-add-sheet"
          aria-label={t(msg`Add sheet`)}
          onClick={() => {
            setWorkbook(addSheet);
            setActiveIdx(workbook().sheets.length - 1);
          }}
        >
          <Plus class="h-4 w-4" />
        </Button>
        <Show when={workbook().sheets.length > 1}>
          <Button
            variant="ghost"
            size="sm"
            class="h-8 text-xs text-muted-foreground"
            data-testid="sheet-remove-sheet"
            onClick={() => {
              const idx = activeIdx();
              setWorkbook((wb) => removeSheet(wb, idx));
              setActiveIdx(Math.max(0, idx - 1));
            }}
          >
            {t(msg`Remove sheet`)}
          </Button>
        </Show>
      </div>
    </div>
  );
}
